<script>
import ResourceBlock from '@components/resources/resource-block.vue'
import MottoIcon from '@components/motto-icon.vue'
import { resourcesComputed } from '@state/helpers'
import { isMobileMixin } from '@src/mixins/isMobileMixin'

export default {
  components: {
    ResourceBlock,
    MottoIcon,
  },
  mixins: [isMobileMixin],
  props: {
    category: {
      type: String,
      required: true,
    },
    resources: {
      type: Array,
      required: true,
    },
    emptySentence: {
      type: String,
      default: 'No resources found',
    },
    minimalInMobile: {
      type: Boolean,
    },
  },
  data() {
    return {
      maxResources: 12,
      perPage: 3,
      pageNumber: 0,
      windowWidth: window.innerWidth,
    }
  },
  computed: {
    ...resourcesComputed,
    getResources() {
      return this.resources
    },
  },
  watch: {
    windowWidth() {
      this.changePerPage()
    },
  },
  mounted() {
    this.$nextTick(() => {
      window.addEventListener('resize', this.onResize)
    })
    this.changePerPage()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    changePageNumber(newPageNumber) {
      this.pageNumber = newPageNumber
    },
    isResourceVisibleInCarousel(resourceIndex, pageNumber, perPage) {
      const max = (pageNumber + 1) * perPage + perPage
      const min = max - perPage * 3
      return resourceIndex >= min && resourceIndex <= max
    },
    changePerPage() {
      if (this.windowWidth > 2462) {
        this.perPage = 6
      } else if (this.windowWidth > 2088) {
        this.perPage = 5
      } else if (this.windowWidth > 1714) {
        this.perPage = 4
      } else {
        this.perPage = 3
      }
    },
    onResize() {
      this.windowWidth = window.innerWidth
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex justify-content-between">
      <h3 :class="$style.title">{{ category }}</h3>
      <MottoIcon
        v-if="resources.length"
        name="Grid"
        icon-color="lightGray"
        class="mr-4 cursor-pointer"
        :class="$style.hideMobile"
        data-open-grid-view
        @click.native="$emit('openGridView')" />
      <MottoIcon
        v-if="resources.length"
        name="List"
        icon-color="lightGray"
        class="mr-4 cursor-pointer"
        :class="$style.showMobile"
        data-open-grid-view-mobile
        @click.native="$emit('openGridView')" />
    </div>
    <p v-if="!resources.length" :class="$style.emptySentence">{{ emptySentence }}</p>
    <carousel
      v-if="resources.length && !isMobile"
      :class="$style.carousel"
      :per-page="perPage"
      :pagination-enabled="false"
      :navigation-enabled="true"
      :navigation-next-label="''"
      :navigation-prev-label="''"
      @page-change="changePageNumber">
      <slide v-for="(resource, index) in getResources" :key="resource.resourceId">
        <ResourceBlock
          v-if="isResourceVisibleInCarousel(index, pageNumber, perPage)"
          :class="{
            [$style.resourceBlock]: true,
            [$style.minimalInMobile]: minimalInMobile,
          }"
          :resource="resource"
          :minimal-in-mobile="minimalInMobile" />
      </slide>
    </carousel>
    <div v-if="resources.length && isMobile" :class="$style.mobileCarousel">
      <div :class="$style.mobileCarouselBlock">
        <ResourceBlock
          v-for="resource in getResources"
          :key="resource.resourceId"
          :class="{
            [$style.resourceBlock]: true,
            [$style.minimalInMobile]: minimalInMobile,
          }"
          :resource="resource"
          :minimal-in-mobile="minimalInMobile" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
@import '@design';

.mobileCarousel {
  @media (max-width: $lg-breakpoint) {
    display: flex;
    flex-wrap: nowrap;
    justify-content: flex-start;
    padding-bottom: 10px;
    overflow-x: scroll;
  }

  display: none;
}

.mobileCarouselBlock {
  display: flex;
  align-content: baseline;
}

.carousel {
  @media (max-width: $lg-breakpoint) {
    display: none;
  }

  > div {
    padding-bottom: 7px;
  }
}

.title {
  margin-bottom: 24px;

  @media (max-width: $lg-breakpoint) {
    font-size: 16px;
  }
}

.emptySentence {
  color: $dark-gray;
  text-align: center;
}

.resourceBlock {
  min-width: 275px;
  max-width: 400px;
  margin-right: 16px;

  @media (max-width: $lg-breakpoint) {
    width: 270px;
  }
}

.minimalInMobile {
  @media (max-width: $lg-breakpoint) {
    width: 127px;
    min-width: 127px;
    max-width: 127px;
  }
}

.showMobile {
  @media (max-width: $lg-breakpoint) {
    display: inline-block;
  }

  display: none;
}

.hideMobile {
  @media (max-width: $lg-breakpoint) {
    display: none;
  }

  display: inline-block;
}
</style>
